import { createSlice } from "@reduxjs/toolkit"
import isEqual from "lodash.isequal"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { RECEIPT_SLICE_NAME as SLICE_NAME } from "./receipt.constants"
import {
  getReceiptExport,
  getReceiptKpi,
  getReceiptLookups,
  getReceiptState
} from "./receipt.thunks"

/* ------------- Initial State ------------- */

const columns = [
  { field_id: "date", label: "Date", active: true },
  { field_id: "card", label: "Card", active: true },
  { field_id: "channel", label: "Channel", active: true },
  { field_id: "program", label: "Program", active: true },
  { field_id: "amount", label: "Amount", active: true },
  { field_id: "fee_charged", label: "Fee charged", active: true }
]

const initialFilters = {
  date: null,
  card: [],
  amount: { min: null, max: null, type: null },
  program: "all",
  channel: "all",
  fee_charged: { min: null, max: null, type: null }
}

const initialState = {
  fetching: false,

  pageState: null, // check RECEIPT_PAGE_STATE
  firstEnrollBlocked: false,
  programs: [],

  selectedProgram: "all",

  kpi: {
    bankInquiries: 0,
    matchRate: 0,
    lookupFees: 0,
    disputeAmountPrevented: 0
  },

  result: {
    count: 0,
    results: []
  },

  filters: initialFilters,

  visibleFilters: {
    date: true,
    card: true,
    amount: true,
    program: true,
    channel: true,
    fee_charged: true
  },

  columns,
  defaultColumns: columns,

  viewMode: "table",

  exportFetching: false
}

/* ------------- Slice ------------- */

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setProp(state, { payload: { key, value } }) {
      if (["filters", "visibleFilters"].includes(key)) {
        state[key] = { ...state[key], ...value }
      } else {
        state[key] = value
      }
    },
    setSelectedProgram(state, { payload }) {
      state.selectedProgram = payload || "all"
    },
    clearFilters(state) {
      state.filters = { ...initialFilters }
    },
    logout(state) {
      const { columns, visibleFilters, viewMode } = state
      Object.assign(state, initialState)
      state.columns = columns
      state.visibleFilters = visibleFilters
      state.viewMode = viewMode
    }
  },
  extraReducers: (builder) => {
    builder
      // getReceiptState
      .addCase(getReceiptState.fulfilled, (state, { payload }) => {
        state.programs = payload?.programs ?? []
        state.pageState = payload?.page_state
        state.firstEnrollBlocked = payload?.first_enroll_blocked ?? false
      })

      .addCase(getReceiptState.rejected, (state) => {
        state.programs = (state?.programs || [])?.map((p) => ({
          ...(p || {}),
          lookup_count: 0,
          status: null
        }))
        state.pageState = null
        state.firstEnrollBlocked = false
        state.kpi = initialState.kpi
        state.result = initialState.result
      })

      // getReceiptKpi
      .addCase(getReceiptKpi.fulfilled, (state, { payload }) => {
        state.kpi = {
          bankInquiries: Number(payload?.bank_inquiries ?? 0),
          matchRate: Number(payload?.match_rate ?? 0),
          lookupFees: Number(payload?.lookup_fees ?? 0),
          disputeAmountPrevented: Number(payload?.dispute_amount_prevented ?? 0)
        }
      })
      .addCase(getReceiptKpi.rejected, (state) => {
        state.kpi = {
          bankInquiries: 0,
          matchRate: 0,
          lookupFees: 0,
          disputeAmountPrevented: 0
        }
      })

      // getReceiptLookups
      .addCase(getReceiptLookups.pending, (state) => {
        state.fetching = true
      })
      .addCase(getReceiptLookups.fulfilled, (state, { payload }) => {
        state.fetching = false
        state.result = {
          count: payload?.count ?? payload?.results?.length ?? 0,
          results: payload?.results ?? []
        }
      })
      .addCase(getReceiptLookups.rejected, (state) => {
        state.fetching = false
        state.result = {
          count: 0,
          results: []
        }
      })

      // getReceiptExport
      .addCase(getReceiptExport.pending, (state) => {
        state.exportFetching = true
      })
      .addCase(getReceiptExport.fulfilled, (state) => {
        state.exportFetching = false
      })
      .addCase(getReceiptExport.rejected, (state) => {
        state.exportFetching = false
      })
  }
})

export const { setProp, setSelectedProgram, clearFilters, logout } = slice.actions

export const INITIAL_KPI = initialState.kpi
export const INITIAL_RESULT = initialState.result

/* ------------- Selectors ------------- */
export const selectReceipt = (state) => state.receipt
export const selectPrograms = (state) => state.receipt.programs
export const selectKpi = (state) => state.receipt.kpi
export const selectSelectedProgram = (state) => state.receipt.selectedProgram
export const selectFilters = (state) => state.receipt.filters
export const selectVisibleFilters = (state) => state.receipt.visibleFilters
export const selectResult = (state) => state.receipt.result
export const selectColumns = (state) => state.receipt.columns
export const selectViewMode = (state) => state.receipt.viewMode

export const selectAreFiltersDefault = (state) => isEqual(initialFilters, state.receipt.filters)

/* ------------- Persistence ------------- */
const PERSISTED_REDUCER_VERSION = 2

export const persistedReducer = persistReducer(
  {
    key: "receiptThunk",
    storage,
    version: PERSISTED_REDUCER_VERSION,
    blacklist: ["fetching", "exportFetching", "kpi", "result", "filters", "selectedProgram"],
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return { ...state, ...initialState }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
